import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { FaExternalLinkAlt } from 'react-icons/fa';

import bnsp from '../assets/BNSP.jpg';
import cflt from '../assets/CFLT.jpg';
import d from '../assets/dummy.png';
import rm from '../assets/Sertifikat Kerja Praktik-Oktaryan Hugo Wiratama.jpg';
import myr from '../assets/mayora.png';

const certificateList = [
  {
    title: 'Web Developer Expert',
    img: bnsp,
    description: 'Certified as a competent Web Developer under the Communication and Informatics scheme.',
    link: 'https://drive.google.com/file/d/1B6_0zT6FkinvXExDrPBA23zPMfm7utcD/view?usp=drive_link',
    issuer: 'BNSP',
    issued: 'April 2024',
    credentialId: '62010 2131 5 0005212 2024',
  },
  {
    title: 'Proficiency English Assessment',
    img: cflt,
    description: 'Has taken English Proficiency Assessment with the following scaled scores: 497.',
    link: 'https://drive.google.com/file/d/1_5Mp1BCBPoozNwKoCc8Fqfqg3FwmSAIy/view?usp=drive_link',
    issuer: 'CFLT',
    issued: 'January 2024',
    credentialId: 'CFLT-20240126-CFLT-00325',
  },
  {
    title: 'Certificate of Internship Completion',
    img: rm,
    description: 'Successfully completed an internship program at CV Rajawali Merah.',
    link: 'https://drive.google.com/file/d/1sykqU6odGmlGEsAr3LKwI-xLHm08sapw/view?usp=drive_link',
    issuer: 'CV. Rajawali Merah',
    issued: 'June 2022',
  },
  {
    title: 'National Webinar: Prepare Your Career With Mayora Group',
    img: myr,
    description: 'Participated in a national webinar organized by Mayora Group and the Career & Alumni Services Unit at Dian Nuswantoro University.',
    link: 'https://drive.google.com/file/d/18SOK0Br3p-o1sVGmAu7FIbca_FymbJL4/view?usp=sharing',
    issuer: 'Mayora Group & Dian Nuswantoro University',
    issued: 'October 2023',
  },
  {
    title: 'Responsive Web Design',
    img: d,
    description: 'Covers the basics of HTML, CSS, Flexbox, Grid, and responsive layouts.',
    link: '#',
    issuer: 'freeCodeCamp',
    issued: '-',
    credentialId: '-',
  },
  {
    title: 'The Web Developer Bootcamp 2025',
    img: d,
    description: 'Complete web development bootcamp covering HTML, CSS, JS, Node, Express, and more.',
    link: '#',
    issuer: 'Udemy',
    issued: '-',
    credentialId: '-',
  },
];

const fadeMotion = {
  visible: (i = 1) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: 'easeOut' },
  }),
  hidden: {
    opacity: 0,
    y: 30,
    transition: { duration: 0.5 },
  },
};

const Certificates = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, amount: 0.4 });

  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [showMoreCertificates, setShowMoreCertificates] = useState(false);

  const getSliceCount = () => {
    const width = window.innerWidth;
    if (width >= 1024) return 6;
    if (width >= 768) return 4;
    return 2;
  };

  const [sliceCount, setSliceCount] = useState(getSliceCount());

  useEffect(() => {
    const handleResize = () => setSliceCount(getSliceCount());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const displayedCertificates = certificateList.slice(0, sliceCount);

  useEffect(() => {
    const root = document.documentElement;
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;

    if (showMoreCertificates || selectedCertificate) {
      root.style.overflow = 'hidden';
      root.style.paddingRight = `${scrollBarWidth}px`;
    } else {
      root.style.overflow = '';
      root.style.paddingRight = '';
    }

    return () => {
      root.style.overflow = '';
      root.style.paddingRight = '';
    };
  }, [showMoreCertificates, selectedCertificate]);

  const handleCertificateClick = (certificate) => setSelectedCertificate(certificate);
  const closePopup = () => setSelectedCertificate(null);
  const closeShowMore = () => setShowMoreCertificates(false);

  return (
    <section id="certificates" className="min-h-screen py-20 px-4 md:px-10 bg-[#F5F3EF] text-[#4B4033]">
      <div className="mb-6 max-w-5xl mx-auto text-center">
        <h2 className="text-2xl font-bold text-[#5D5044] mb-4">Certifications</h2>
      </div>

      <div className="max-w-6xl mx-auto px-4">
        <div className="flex justify-start gap-4 mb-6 pl-2 flex-wrap">
          {certificateList.length > sliceCount && (
            <motion.button
              onClick={() => setShowMoreCertificates(true)}
              whileHover={{ scale: 1.07, transition: { type: 'spring', stiffness: 300 } }}
              className="px-4 py-2 bg-[#8C6D5A] hover:bg-[#7B5E4C] text-white text-sm font-semibold rounded-lg cursor-pointer"
            >
              Show More
            </motion.button>
          )}
        </div>

        <div ref={containerRef} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {displayedCertificates.map((certificate, idx) => (
            <motion.div
              key={idx}
              className="group relative bg-[#E4D9CE] bg-opacity-80 rounded-xl overflow-hidden cursor-pointer border-2 border-[#C8B8A8] w-full max-w-sm aspect-auto mx-auto shadow-md hover:shadow-lg"
              onClick={() => handleCertificateClick(certificate)}
              custom={idx}
              initial="hidden"
              animate={isInView ? 'visible' : 'hidden'}
              variants={fadeMotion}
              whileHover={{ scale: 1.03 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <img
                src={certificate.img}
                alt={certificate.title}
                className="w-full h-52 object-cover transition duration-300 group-hover:brightness-50 group-hover:blur-sm"
              />
              <motion.div className="absolute inset-0 flex flex-col justify-between bg-black bg-opacity-40 text-white p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute top-2 right-2 bg-[#F5F3EF] text-[#4B4033] px-2 py-1 rounded text-xs font-semibold shadow-sm">
                  {certificate.issuer.toUpperCase()}
                </div>
                <div className="mt-auto">
                  <p className="text-sm mb-2 line-clamp-3">{certificate.description}</p>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Show More Certificates Modal */}
      <AnimatePresence>
        {showMoreCertificates && (
          <motion.div
            className="fixed inset-0 z-[100] bg-black bg-opacity-80 backdrop-blur-sm flex items-center justify-center p-5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeShowMore}
          >
            <motion.div
              className="bg-[#E9E1D8] rounded-2xl p-6 max-w-7xl w-full max-h-[90vh] overflow-y-auto shadow-lg relative"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.4 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button onClick={closeShowMore} className="absolute top-3 right-3 p-2 text-[#4B4033] hover:text-[#7B5E4C] font-bold text-xl">
                ×
              </button>
              <h3 className="text-2xl font-semibold text-[#5D5044] mb-6 text-center">All Certificates</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {certificateList.map((certificate, idx) => (
                  <motion.div
                    key={idx}
                    className="group relative bg-[#E4D9CE] bg-opacity-80 rounded-xl overflow-hidden cursor-pointer border-2 border-[#C8B8A8] w-full aspect-auto mx-auto shadow-md hover:shadow-lg"
                    onClick={() => handleCertificateClick(certificate)}
                    whileHover={{ scale: 1.03 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    <img
                      src={certificate.img}
                      alt={certificate.title}
                      className="w-full h-48 object-cover transition duration-300 group-hover:brightness-50 group-hover:blur-sm"
                    />
                    <motion.div className="absolute inset-0 flex flex-col justify-between bg-black bg-opacity-40 text-white p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute top-2 right-2 bg-[#F5F3EF] text-[#4B4033] px-2 py-1 rounded text-xs font-semibold shadow-sm">
                        {certificate.issuer.toUpperCase()}
                      </div>
                      <div className="mt-auto">
                        <h4 className="font-bold text-lg mb-1">{certificate.title}</h4>
                        <p className="text-sm mb-2 line-clamp-3">{certificate.description}</p>
                      </div>
                    </motion.div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Certificate Detail Popup */}
      <AnimatePresence>
        {selectedCertificate && (
          <motion.div
            className="fixed inset-0 z-[110] bg-black bg-opacity-80 backdrop-blur-sm flex items-center justify-center p-5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closePopup}
          >
            <motion.div
              className="bg-[#E9E1D8] rounded-2xl p-6 max-w-xl max-h-[90vh] overflow-y-auto shadow-lg relative"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.4 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closePopup}
                className="absolute top-3 right-3 p-2 text-[#4B4033] hover:text-[#7B5E4C] font-bold text-xl"
              >
                &times;
              </button>
              <h3 className="text-[#4B4033] text-2xl font-semibold mb-4">{selectedCertificate.title}</h3>
              <img
                src={selectedCertificate.img}
                alt={selectedCertificate.title}
                className="w-full max-h-64 object-cover rounded-md mb-4"
              />
              <p className="text-[#4B4033] mb-2">{selectedCertificate.description}</p>
              <p className="text-sm text-[#7B5E4C] mb-1"><strong>Issuer:</strong> {selectedCertificate.issuer}</p>
              <p className="text-sm text-[#7B5E4C] mb-1"><strong>Issued:</strong> {selectedCertificate.issued}</p>
              {selectedCertificate.credentialId && selectedCertificate.credentialId !== '-' && (
                <p className="text-sm text-[#7B5E4C] mb-1"><strong>ID:</strong> {selectedCertificate.credentialId}</p>
              )}
              {selectedCertificate.link && selectedCertificate.link !== '#' && (
                <a
                  href={selectedCertificate.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#4B4033] hover:text-[#7B5E4C] mt-3"
                >
                  <FaExternalLinkAlt />
                  View Certificate
                </a>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Certificates;
